import { createFileRoute } from '@tanstack/react-router';
import { useIxmUserauthV1RealmQueryServiceRealmGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-realm-query-service-realm-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN, WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1RealmState } from '@interxfi/types';
import { RealmBreadcrumb } from '../../../../../../features/userauth/realm/userauth-realm-breadcrumb';
import { UserauthRealmEntityMenu } from '../../../../../../features/userauth/realm/userauth-realm-entity-menu';
import { UserauthRealmEventSummary } from '../../../../../../features/userauth/realm/userauth-realm-event';
import { getUserauthRealmStateListItems } from '../../../../../../features/userauth/realm/userauth-realm-list-items';
import { UserauthRealmStatusPill } from '../../../../../../features/userauth/realm/userauth-realm-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_platform/_context-required/realm/_realm/$realmId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `realm-${params.realmId}`,
        label: <RealmBreadcrumb realmId={params.realmId} />,
        to: `/realm/${params.realmId}`,
        suffix: <CopyButton content={params.realmId} label="Copy Realm ID" tooltip={{ copy: params.realmId, copied: 'Copied Realm ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Realm'),
  component: RealmDetail,
});

function getListItems(data: IxmUserauthV1RealmState | undefined) {
  const items = getUserauthRealmStateListItems(data);
  const tenantTypes = (items.data.spec.tenantTypes ?? []).map((tt) => [tt.label, tt.name, tt.singular] as const satisfies ReadonlyListItem[]);

  return {
    left: [items.realmId, items.data.spec.baseUrl, items.metadata.createdAt, items.metadata.updatedAt] as const satisfies ReadonlyListItem[],
    tenantTypes,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load realm', 'view this realm');

function RealmDetail() {
  const { realmId } = Route.useParams();
  const request = { realmId } as const;
  const { data, error, isPending } = useIxmUserauthV1RealmQueryServiceRealmGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = getListItems(data?.realm);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <UserauthRealmStatusPill isPending={showPending} size="large" status={data?.realm?.status} />
          </HorizontalStack>

          <UserauthRealmEntityMenu realmId={realmId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <UserauthRealmEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <VerticalStack gap={4}>
                  {listItems.tenantTypes.map((ttItems) => (
                    <BoxedAppReadonlyList isPending={showPending} items={ttItems} key={ttItems[0].id} />
                  ))}
                </VerticalStack>
              ),
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
